import React, { useState, useEffect } from 'react';
import { Plus, Truck, Edit, Trash2, ChevronDown, ChevronUp, Package, X, Star } from 'lucide-react';
import { Th, Td } from '../WMSShared';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wms';

export default function SuppliersTab({ suppliers, setEditItem, setForm, setModal, delSupplier }) {
  const [expanded, setExpanded] = useState(null);
  const [supProducts, setSupProducts] = useState({});
  const [allProducts, setAllProducts] = useState([]);
  const [addingTo, setAddingTo] = useState(null);
  const [addForm, setAddForm] = useState({ product_id: '', unit_cost: '', lead_time_days: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setAllProducts(r.data)).catch(() => {});
  }, []);

  const loadSupplierProducts = async (supplierId) => {
    try {
      const r = await axios.get(`${API}/supplier-products/${supplierId}`);
      setSupProducts(prev => ({ ...prev, [supplierId]: r.data }));
    } catch {}
  };

  const toggleExpand = (supplierId) => {
    if (expanded === supplierId) {
      setExpanded(null);
    } else {
      setExpanded(supplierId);
      if (!supProducts[supplierId]) loadSupplierProducts(supplierId);
    }
  };

  const addProduct = async (supplierId) => {
    if (!addForm.product_id || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/supplier-products`, {
        supplier_id: supplierId,
        product_id: addForm.product_id,
        unit_cost: parseFloat(addForm.unit_cost) || 0,
        lead_time_days: parseInt(addForm.lead_time_days) || 7,
      });
      setAddForm({ product_id: '', unit_cost: '', lead_time_days: '' });
      setAddingTo(null);
      await loadSupplierProducts(supplierId);
    } catch {}
    setSaving(false);
  };

  const removeProduct = async (supplierId, productId) => {
    try {
      await axios.delete(`${API}/supplier-products/${supplierId}/${productId}`);
      await loadSupplierProducts(supplierId);
    } catch {}
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Suppliers <span className="text-gray-400 font-normal text-base">({suppliers.length})</span></h2>
        <button onClick={() => { setEditItem(null); setForm({ city: 'Singapore', country: 'Singapore', payment_terms: 'Net 30', lead_time_days: 7, is_active: true }); setModal('supplier'); }}
          className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 shadow-sm"
          data-testid="add-supplier-btn">
          <Plus className="w-4 h-4" />Add Supplier
        </button>
      </div>
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr><Th>Supplier</Th><Th>Company</Th><Th>Contact</Th><Th>Payment</Th><Th>Lead Time</Th><Th>Products</Th><Th>Actions</Th></tr>
            </thead>
            <tbody className="divide-y">
              {suppliers.map(s => {
                const isOpen = expanded === s.supplier_id;
                const products = supProducts[s.supplier_id] || [];
                return (
                  <React.Fragment key={s.supplier_id}>
                    <tr className={`hover:bg-gray-50 cursor-pointer transition-colors ${isOpen ? 'bg-blue-50/50' : ''}`}
                        onClick={() => toggleExpand(s.supplier_id)} data-testid={`supplier-row-${s.supplier_id}`}>
                      <Td>
                        <div className="flex items-center gap-1.5">
                          {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-blue-500 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                          <div>
                            <p className="font-medium">{s.name}</p>
                            <p className="text-[10px] text-gray-400 font-mono">{s.supplier_id}</p>
                          </div>
                        </div>
                      </Td>
                      <Td className="text-gray-600 text-xs">{s.company || '-'}</Td>
                      <Td><div><p className="text-xs">{s.email}</p><p className="text-xs text-gray-400">{s.phone}</p></div></Td>
                      <Td className="text-xs">{s.payment_terms}</Td>
                      <Td className="text-xs">{s.lead_time_days}d</Td>
                      <Td>
                        {isOpen && products.length > 0 ? (
                          <span className="text-xs font-semibold text-blue-600">{products.length} products</span>
                        ) : (
                          <span className="text-xs text-gray-400">{products.length || '—'}</span>
                        )}
                      </Td>
                      <Td onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <button onClick={() => { setEditItem(s); setForm(s); setModal('supplier'); }} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-500"><Edit className="w-3.5 h-3.5" /></button>
                          <button onClick={() => delSupplier(s.supplier_id)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </Td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50/80">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                              <Package className="w-3.5 h-3.5" /> Products Supplied ({products.length})
                            </p>
                            <button onClick={(e) => { e.stopPropagation(); setAddingTo(addingTo === s.supplier_id ? null : s.supplier_id); setAddForm({ product_id: '', unit_cost: '', lead_time_days: String(s.lead_time_days || 7) }); }}
                              className="text-xs bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 font-medium flex items-center gap-1"
                              data-testid={`add-product-to-${s.supplier_id}`}>
                              <Plus className="w-3 h-3" /> Add Product
                            </button>
                          </div>

                          {/* Add product form */}
                          {addingTo === s.supplier_id && (
                            <div className="bg-white border rounded-lg p-3 mb-3 flex items-end gap-3" data-testid="add-supplier-product-form">
                              <div className="flex-1">
                                <label className="text-[10px] font-semibold text-gray-500 uppercase">Product</label>
                                <select value={addForm.product_id} onChange={e => {
                                  const pid = e.target.value;
                                  const prod = allProducts.find(p => p.product_id === pid);
                                  setAddForm(f => ({ ...f, product_id: pid, unit_cost: prod ? String(prod.cost || '') : '' }));
                                }}
                                  className="w-full mt-1 text-sm border rounded-lg px-3 py-1.5 bg-white">
                                  <option value="">Select product...</option>
                                  {allProducts
                                    .filter(p => !products.some(sp => sp.product_id === p.product_id))
                                    .map(p => <option key={p.product_id} value={p.product_id}>{p.name} ({p.sku})</option>)}
                                </select>
                              </div>
                              <div className="w-28">
                                <label className="text-[10px] font-semibold text-gray-500 uppercase">Unit Cost (S$)</label>
                                <input type="number" min="0" step="0.01" value={addForm.unit_cost}
                                  onChange={e => setAddForm(f => ({ ...f, unit_cost: e.target.value }))}
                                  className="w-full mt-1 text-sm border rounded-lg px-3 py-1.5" placeholder="0.00" />
                              </div>
                              <div className="w-24">
                                <label className="text-[10px] font-semibold text-gray-500 uppercase">Lead Time</label>
                                <input type="number" min="1" value={addForm.lead_time_days}
                                  onChange={e => setAddForm(f => ({ ...f, lead_time_days: e.target.value }))}
                                  className="w-full mt-1 text-sm border rounded-lg px-3 py-1.5" placeholder="7" />
                              </div>
                              <button onClick={() => addProduct(s.supplier_id)} disabled={!addForm.product_id || saving}
                                className="text-xs bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 font-medium disabled:opacity-50 whitespace-nowrap">
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button onClick={() => setAddingTo(null)} className="text-gray-400 hover:text-gray-700 p-1">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}

                          {/* Products list */}
                          {products.length > 0 ? (
                            <div className="bg-white rounded-lg border divide-y">
                              {products.map(sp => (
                                <div key={sp.product_id} className="flex items-center justify-between px-3 py-2.5 text-xs">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-gray-800">{sp.product_name}</p>
                                      {sp.is_preferred && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-0.5">SKU: {sp.sku} · {sp.product_id}</p>
                                  </div>
                                  <div className="flex items-center gap-6 shrink-0">
                                    <div className="text-right">
                                      <p className="font-semibold">S${(sp.unit_cost || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                                      <p className="text-[10px] text-gray-400">per unit</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold">{sp.lead_time_days || 7}d</p>
                                      <p className="text-[10px] text-gray-400">lead time</p>
                                    </div>
                                    <button onClick={() => removeProduct(s.supplier_id, sp.product_id)}
                                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                                      data-testid={`remove-sp-${sp.product_id}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 text-center py-3">No products assigned. Click "Add Product" to define what this supplier sells.</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {suppliers.length === 0 && <div className="p-8 text-center text-gray-400"><Truck className="w-8 h-8 mx-auto mb-2" /><p>No suppliers yet. Add your first supplier.</p></div>}
      </div>
    </div>
  );
}
